import { useState, useCallback, useEffect, useRef, memo, lazy, Suspense, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '../../../constants/routes';
import { ConnectorTypeEnum } from '../../../shared/enums/connector-type';
import {
  StationStatus,
  ConnectorStatus,
  getStationStatusLabel,
  getStationStatusClass,
} from '../../../shared/enums/status';
import { hasPermission, PERMISSION } from '../../../shared/utils/permissions';
import { useUser } from '../../../hooks/useUser';
import { useStationQuery, dashboardQueryKeys } from '../../../hooks/useDashboardQueries';
import '../CreateStation/create-station.css';
import './station-details.css';
import { PrivacyToggle } from '../../../components/common/PrivacyToggle/PrivacyToggle';

// bundle-dynamic-imports: Lazy load tab components for better initial load
const EditStationTab = lazy(() => import('./components/EditStationTab'));
const OcppTerminalTab = lazy(() => import('./components/OcppTerminalTab'));
const ConnectorsTab = lazy(() => import('./components/ConnectorsTab'));
const StationLogsTab = lazy(() => import('./components/StationLogsTab/StationLogsTab'));

// rendering-hoist-jsx: Hoist static loading fallback
const TabLoadingFallback = (
  <div className="tab-loading">
    <div className="loading-spinner" />
  </div>
);

export interface StationData {
  id: string;
  idTag: string;
  status: StationStatus;
  connectorTypes: ConnectorTypeEnum[];
  option: number;
  price: number;
  address: string;
  latitude: number;
  longitude: number;
  aroundTheClock: boolean;
  startWorkingTime?: string;
  endWorkingTime?: string;
  connectors: ConnectorData[];
  currentLimit?: number;
  loadBalancingStrategy?: import('../../../shared/enums/load-balancing-strategy.enum').LoadBalancingStrategyEnum;
  downtimePricePerMinute?: number;
  downtimeFreeMinutes?: number;
  organizationId?: string;
  organizationName?: string;
}

export interface ConnectorData {
  id: number;
  status: ConnectorStatus;
  energy: number;
  power: number;
  current: number;
  voltage: number;
}

type TabType = 'edit' | 'connectors' | 'logs' | 'ocpp';

const TAB_DEFS = [
  {
    id: 'edit' as const,
    labelKey: 'dashboard.stationDetails.tabs.edit',
    icon: 'edit',
    permission: PERMISSION.STATIONS_TAB_EDIT,
  },
  {
    id: 'connectors' as const,
    labelKey: 'dashboard.stationDetails.tabs.connectors',
    icon: 'ev_station',
    permission: PERMISSION.STATIONS_TAB_CONNECTORS,
  },
  {
    id: 'logs' as const,
    labelKey: 'dashboard.stationDetails.tabs.logs',
    icon: 'description',
    permission: PERMISSION.STATIONS_TAB_LOGS,
  },
  {
    id: 'ocpp' as const,
    labelKey: 'dashboard.stationDetails.tabs.ocpp',
    icon: 'terminal',
    permission: PERMISSION.STATIONS_TAB_OCPP,
  },
] as const;

// OCPP roundtrip latency: the backend only updates connector status after
// the station replies to RemoteStart/Stop/ChangeAvailability (1-3s). We do
// a fixed, bounded schedule of refetches — no polling loop, no recursion,
// hard cap of REFRESH_SCHEDULE_MS.length attempts per action.
const REFRESH_SCHEDULE_MS = [0, 1500, 3500] as const;

export const StationDetails = memo(function StationDetails() {
  const { stationId } = useParams<{ stationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  // Hide tabs the caller has no permission for. Backend re-enforces on every
  // endpoint — this is purely a UI gate so users don't see dead controls.
  const tabs = useMemo(
    () =>
      TAB_DEFS.filter((tab) => hasPermission(user?.permissions, tab.permission)).map(
        (tab) => ({ id: tab.id, label: t(tab.labelKey), icon: tab.icon }),
      ),
    [t, user?.permissions],
  );
  const allowedTabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const [requestedTab, setActiveTab] = useState<TabType>('edit');
  // Derived, not synced: when permissions drop the requested tab, fall back
  // to the first allowed one without an effect/re-render cycle.
  const activeTab = allowedTabIds.includes(requestedTab)
    ? requestedTab
    : allowedTabIds[0];

  const { data: station, isLoading: loading, isError, refetch } = useStationQuery(stationId);

  const refreshTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const cancelPendingRefreshes = useCallback(() => {
    refreshTimersRef.current.forEach(clearTimeout);
    refreshTimersRef.current = [];
  }, []);

  // Cancel any pending refetches when the page unmounts so we don't leak timers.
  useEffect(() => cancelPendingRefreshes, [cancelPendingRefreshes]);

  const handleConnectorRefresh = useCallback(() => {
    // A new action supersedes any pending refresh from the previous one,
    // so back-to-back clicks don't compound timers.
    cancelPendingRefreshes();
    for (const delay of REFRESH_SCHEDULE_MS) {
      const timer = setTimeout(() => {
        // refetch() only toggles isFetching (not isLoading), so the page
        // doesn't flash a spinner — same as the old silent fetch.
        refetch();
      }, delay);
      refreshTimersRef.current.push(timer);
    }
  }, [refetch, cancelPendingRefreshes]);

  const handleBack = useCallback(() => {
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_STATIONS}`);
  }, [navigate]);

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
  }, []);

  const handleStationUpdate = useCallback((updatedStation: Partial<StationData>) => {
    queryClient.setQueryData<StationData>(
      dashboardQueryKeys.station(stationId ?? ''),
      (prev) => (prev ? { ...prev, ...updatedStation } : prev),
    );
  }, [queryClient, stationId]);

  if (loading) {
    return (
      <div className="station-details-page">
        <div className="station-details-loading">
          <div className="loading-spinner" />
          <p>{t('dashboard.stationDetails.loading')}</p>
        </div>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="station-details-page">
        <div className="station-details-error">
          <span className="material-symbols-outlined">error</span>
          <p>
            {isError
              ? t('dashboard.stationDetails.loadFailed')
              : t('dashboard.stationDetails.stationNotFound')}
          </p>
          <button onClick={handleBack} className="btn-back-error">
            {t('dashboard.stationDetails.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="station-details-page">
      {/* Header */}
      <header className="station-details-header">
        <div className="header-left">
          <button
            type="button"
            className="btn-back"
            onClick={handleBack}
            aria-label={t('dashboard.stationDetails.backAria')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
          </button>
          <div className="station-identity">
            <h1 className="station-id">{station.idTag}</h1>
            <span className="station-label">{t('dashboard.stationDetails.stationLabel')}</span>
            {station.organizationName && (
              <Link
                className="station-label"
                to={`/${ROUTES.DASHBOARD}/organizations/${station.organizationId}/stations`}
              >
                {station.organizationName}
              </Link>
            )}
          </div>
        </div>
        <div className="header-right">
          <PrivacyToggle scope="stationDetails" />
          <div
            className={`status-badge ${getStationStatusClass(station.status)}`}
            role="status"
            aria-label={t('dashboard.stationDetails.statusAria', { label: getStationStatusLabel(station.status) })}
          >
            <span className="status-label">{t('dashboard.stationDetails.statusLabel')}</span>
            <span className="status-value">{getStationStatusLabel(station.status)}</span>
          </div>
        </div>
      </header>

      {/* Tabs - Accessibility: proper tab role attributes */}
      <nav className="station-tabs" role="tablist" aria-label={t('dashboard.stationDetails.tabsAria')}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="material-symbols-outlined tab-icon" aria-hidden="true">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* Tab Content - bundle-conditional: Only render active tab */}
      <main className="station-details-content">
        {tabs.length === 0 ? (
          <div className="station-details-error">
            <span className="material-symbols-outlined">lock</span>
            <p>{t('dashboard.stationDetails.noTabPermission')}</p>
          </div>
        ) : (
          <Suspense fallback={TabLoadingFallback}>
            {activeTab === 'edit' && (
              <EditStationTab
                station={station}
                onUpdate={handleStationUpdate}
              />
            )}
            {activeTab === 'connectors' && (
              <ConnectorsTab
                connectors={station.connectors}
                stationId={station.idTag}
                stationDbId={station.id}
                onRefresh={handleConnectorRefresh}
              />
            )}
            {activeTab === 'logs' && (
              <StationLogsTab stationId={station.idTag} />
            )}
            {activeTab === 'ocpp' && (
              <OcppTerminalTab
                stationId={station.idTag}
              />
            )}
          </Suspense>
        )}
      </main>
    </div>
  );
});

export default StationDetails;
